/* eslint-disable no-console */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, PutEffect, select, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import api from '@/api/_legacy'
import { GetUserNameCommand, UserInformationBasic } from '@/api/_legacy/generated-api'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { Errors, extractErrorMessagesFromServerResponseToArray } from '../../tradebuilder-v2/util'
import { BankUser } from '../types'
import { mapBankUserToUserApiCreateRequest, mapBankUserToUserApiEditRequest } from './mappers'
import { validate } from './validation-rules'

function mapToBankUser(userDetails: UserInformationBasic): BankUser {
  const user = {
    firstName: userDetails.firstName,
    lastName: userDetails.lastName,
    userName: userDetails.userName,
    role: userDetails.roles[0],
    email: userDetails.email,
    userId: userDetails.userId
  }
  return user
}

function filterUsers(users: BankUser[], st: string, rf: string[]): BankUser[] {
  const usersFilteredByRole = rf.length === 0 ? users : users.filter(u => rf.includes(u.role))
  const usersFilteredBySearch = usersFilteredByRole.filter(u =>
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(st.toLowerCase())
  )
  return usersFilteredBySearch
}

export type BankUsersState = {
  availableRoles: string[]
  canDelete: boolean
  canProceed: boolean
  errors: Errors
  filteredUsers: BankUser[]
  isFiltering: boolean
  isNew: boolean
  numTotalUsers: number
  roleFilter: string[]
  saveSuccess: boolean
  searchTerm: string
  selectedUser?: BankUser
  serverErrors?: string[]
  users: BankUser[]
  verificationUsername?: string
}

const initialState: BankUsersState = {
  numTotalUsers: 0,
  users: [],
  filteredUsers: [],
  canProceed: false,
  canDelete: false,
  saveSuccess: false,
  isNew: false,
  errors: {},
  availableRoles: [],
  searchTerm: '',
  roleFilter: [],
  isFiltering: false
}

export const bankUsersSlice = createSlice({
  name: 'bankUsers',
  initialState,
  reducers: {
    init: () => {
      // noop
    },
    fetchBankUsersSuccess: (state, action: PayloadAction<UserInformationBasic[]>) => {
      const rawUsers = action.payload
      state.users = rawUsers.map(mapToBankUser)
      state.filteredUsers = state.users
      state.numTotalUsers = state.users.length
    },
    fetchRolesSuccess: (state, action: PayloadAction<string[]>) => {
      state.availableRoles = action.payload
    },
    saveError: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    saveUser: () => {
      console.log('saving user...')
    },
    deleteUser: () => {
      console.log('deleting user...')
    },
    saveUserSuccess: state => {
      state.canProceed = false
      state.isNew = false
      state.saveSuccess = true
    },
    deleteUserSuccess: state => {
      state.canProceed = false
      state.canDelete = false
      state.isNew = false
      state.selectedUser = null
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload
      state.filteredUsers = filterUsers(state.users, state.searchTerm, state.roleFilter)
      state.numTotalUsers = state.filteredUsers.length
    },
    setRoleFilter: (state, action: PayloadAction<string>) => {
      if (state.roleFilter.includes(action.payload)) {
        state.roleFilter = state.roleFilter.filter(x => x !== action.payload)
      } else {
        state.roleFilter.push(action.payload)
      }
      state.filteredUsers = filterUsers(state.users, state.searchTerm, state.roleFilter)
      state.numTotalUsers = state.filteredUsers.length
    },
    setFilteringState: state => {
      state.isFiltering = !state.isFiltering
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setSelectedUser: (state, action: PayloadAction<string>) => {
      state.selectedUser = state.users.find(x => x.userId === action.payload)
      state.isNew = false
      state.verificationUsername = ''
      state.errors = {}
      state.serverErrors = []
    },
    setNewUser: state => {
      state.isNew = true
      state.selectedUser = {
        firstName: '',
        lastName: '',
        email: '',
        userName: '',
        userId: '',
        role: ''
      }
      state.errors = {}
      state.serverErrors = []
    },
    setFirstName: (state, action: PayloadAction<string>) => {
      state.selectedUser.firstName = action.payload
      state.canProceed = true
      state.saveSuccess = false
      delete state.errors.first_name
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.selectedUser.lastName = action.payload
      state.canProceed = true
      state.saveSuccess = false
      delete state.errors.last_name
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.selectedUser.email = action.payload
      state.canProceed = true
      state.saveSuccess = false
      delete state.errors.email
    },
    setRole: (state, action: PayloadAction<string>) => {
      state.selectedUser.role = action.payload
      state.canProceed = true
      state.saveSuccess = false
      delete state.errors.role
    },
    setVerificationUsername: (state, action: PayloadAction<{ input: string; target: string }>) => {
      state.verificationUsername = action.payload.input
      if (action.payload.input === action.payload.target) {
        state.canDelete = true
      } else {
        state.canDelete = false
      }
      state.saveSuccess = false
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const { actions } = bankUsersSlice
export type bankUserActions = typeof actions

function saveUserSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveUser2Saga() {
    const state: RootState = yield select()
    const { bankUsers } = state
    const { selectedUser } = bankUsers

    const result = validate(bankUsers)
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      try {
        if (bankUsers.isNew) {
          const getUsernameRequest: GetUserNameCommand = {
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName
          }
          const username = yield call(api.usermanager.usernameGenerateCreate, getUsernameRequest)
          const createUserRequest = mapBankUserToUserApiCreateRequest(
            selectedUser,
            username.data.userName
          )
          yield call(api.usermanager.createCreate, createUserRequest, { format: 'json' })
          yield onSuccess
        } else {
          const oldRole = bankUsers.users.find(x => x.userId === selectedUser.userId).role
          const editUserRequest = mapBankUserToUserApiEditRequest(selectedUser, oldRole)
          yield call(api.usermanager.usermanagerUpdate, editUserRequest)
          yield onSuccess
        }
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

function deleteUserSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* deleteUser2Saga() {
    const state: RootState = yield select()
    const { bankUsers } = state
    const { selectedUser } = bankUsers

    const result = validate(bankUsers)
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      try {
        yield call(api.usermanager.removeDelete, selectedUser.userName)
        yield onSuccess
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

function* fetchBankUsersSaga() {
  try {
    const response = yield call(api.usermanager.bankAllList)
    yield put({
      type: 'bankUsers/fetchBankUsersSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'bankUsers/fetchBankUsersFailure', payload: error })
  }
}

function* fetchAvailableRolesSaga() {
  try {
    const response = yield call(api.usermanager.rolesBankAllList)
    yield put({
      type: 'bankUsers/fetchRolesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'bankUsers/fetchRolesFailure', payload: error })
  }
}

function* fetchInitialDataSaga() {
  yield all([fetchBankUsersSaga(), fetchAvailableRolesSaga()])
}

export function* bankUsersRootSaga() {
  yield all([
    takeLatest('bankUsers/init', fetchInitialDataSaga),
    takeLatest(
      'bankUsers/saveUser',
      saveUserSaga(put(actions.saveUserSuccess()), e => put(actions.saveError(e)))
    ),
    takeLatest(
      'bankUsers/deleteUser',
      deleteUserSaga(put(actions.deleteUserSuccess()), e => put(actions.saveError(e)))
    ),
    takeLatest('bankUsers/saveUserSuccess', fetchBankUsersSaga),
    takeLatest('bankUsers/deleteUserSuccess', fetchBankUsersSaga)
  ])
}

export default bankUsersSlice.reducer
